import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createUser,
  deleteUser,
  disableUser,
  getUserById,
  getUsers,
  getUsersByClient,
  inviteUser,
  updateUser,
} from '../services/userService';
import type { CreateUserInput, InviteUserInput, ManagedUser, UpdateUserInput } from '../types/user';

export function useUsers(companyId?: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: companyId ? ['users', companyId] : ['users'],
    queryFn: () =>
      (companyId ? getUsersByClient(companyId) : getUsers()),
    enabled: options?.enabled ?? true,
  });
}

export function useUser(id?: string) {
  return useQuery({
    queryKey: ['user', id],
    queryFn: () => getUserById(id ?? ''),
    enabled: !!id,
  });
}

export function useUserByCognitoIdentity(sub?: string, username?: string) {
  return useQuery({
    queryKey: ['user', 'cognito', sub, username],
    queryFn: async () => {
      if (!sub) {
        throw new Error('User id is missing.');
      }

      try {
        return await getUserById(sub);
      } catch (error) {
        if (!username || username === sub) {
          throw error;
        }

        return getUserById(username);
      }
    },
    enabled: !!sub,
  });
}

export function useInviteUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: InviteUserInput) => inviteUser(input),
    onSuccess: (createdUser) => {
      queryClient.setQueryData<ManagedUser[]>(['users'], (users = []) => [
        createdUser,
        ...users.filter((user) => user.id !== createdUser.id),
      ]);
      void queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

export function useCreateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateUserInput) => createUser(input),
    onSuccess: (createdUser) => {
      queryClient.setQueryData<ManagedUser[]>(['users'], (users = []) => [
        createdUser,
        ...users.filter((user) => user.id !== createdUser.id),
      ]);
      void queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateUserInput }) =>
      updateUser(id, input),
    onMutate: async ({ id, input }) => {
      await queryClient.cancelQueries({ queryKey: ['users'] });

      const previousUsers = queryClient.getQueriesData<ManagedUser[]>({ queryKey: ['users'] });

      queryClient.setQueriesData<ManagedUser[]>({ queryKey: ['users'] }, (users = []) =>
        users.map((user) => {
          if (user.id !== id) {
            return user;
          }

          const disabled =
            input.disabled ??
            (typeof input.enabled === 'boolean' ? !input.enabled : user.disabled);

          return {
            ...user,
            name: input.name ?? user.name,
            role: input.role ?? user.role,
            companyId: input.companyId ?? user.companyId,
            disabled,
            status:
              typeof disabled === 'boolean'
                ? disabled
                  ? 'disabled'
                  : 'active'
                : user.status,
          };
        }),
      );

      return { previousUsers };
    },
    onError: (_error, _variables, context) => {
      context?.previousUsers.forEach(([queryKey, users]) => {
        queryClient.setQueryData(queryKey, users);
      });
    },
    onSuccess: (updatedUser) => {
      queryClient.setQueryData<ManagedUser[]>(['users'], (users = []) =>
        users.map((user) => (user.id === updatedUser.id ? updatedUser : user)),
      );
      queryClient.setQueryData<ManagedUser[]>(['users', updatedUser.companyId], (users = []) =>
        users.map((user) => (user.id === updatedUser.id ? updatedUser : user)),
      );
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

export function useDisableUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => disableUser(id),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: ['users'] });

      const previousUsers = queryClient.getQueriesData<ManagedUser[]>({ queryKey: ['users'] });

      queryClient.setQueriesData<ManagedUser[]>({ queryKey: ['users'] }, (users = []) =>
        users.map((user) =>
          user.id === id
            ? {
                ...user,
                disabled: true,
                status: 'disabled',
              }
            : user,
        ),
      );

      return { previousUsers };
    },
    onError: (_error, _id, context) => {
      context?.previousUsers.forEach(([queryKey, users]) => {
        queryClient.setQueryData(queryKey, users);
      });
    },
    onSuccess: (updatedUser) => {
      queryClient.setQueryData<ManagedUser[]>(['users'], (users = []) =>
        users.map((user) => (user.id === updatedUser.id ? updatedUser : user)),
      );
      queryClient.setQueryData<ManagedUser[]>(['users', updatedUser.companyId], (users = []) =>
        users.map((user) => (user.id === updatedUser.id ? updatedUser : user)),
      );
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

export function useDeleteUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteUser(id),
    onSuccess: (_result, deletedUserId) => {
      queryClient.setQueryData<ManagedUser[]>(['users'], (users = []) =>
        users.filter((user) => user.id !== deletedUserId),
      );
      void queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
}
